import { clsx, type ClassValue } from "clsx";
import { formatDistanceToNowStrict, format as formatDateFns } from "date-fns";
import { twMerge } from "tailwind-merge";

const imageExtensions = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "heic", "heif"]);
const videoExtensions = new Set(["mp4", "mov", "avi", "mkv", "webm", "m4v"]);
const pdfExtensions = new Set(["pdf"]);
const spreadsheetExtensions = new Set(["xls", "xlsx", "csv", "tsv", "ods"]);
const documentExtensions = new Set(["doc", "docx", "odt", "rtf", "txt", "md"]);
const presentationExtensions = new Set(["ppt", "pptx", "odp", "key"]);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBytes(value: number) {
  if (value === 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const amount = value / 1024 ** exponent;
  return `${amount.toFixed(amount >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

export function formatDate(value: string | Date, pattern = "MMM d, yyyy - HH:mm") {
  return formatDateFns(new Date(value), pattern);
}

export function formatRelativeTime(value: string | Date) {
  return formatDistanceToNowStrict(new Date(value), { addSuffix: true });
}

export function getFileExtension(fileName: string) {
  const lastDot = fileName.lastIndexOf(".");
  return lastDot >= 0 ? fileName.slice(lastDot + 1).toLowerCase() : "";
}

export function getMimeCategory(mimeType?: string, fileName?: string) {
  const normalizedMime = mimeType?.toLowerCase() ?? "";
  const extension = fileName ? getFileExtension(fileName) : "";

  if (normalizedMime.startsWith("image/") || imageExtensions.has(extension)) {
    return "image";
  }
  if (normalizedMime.startsWith("video/") || videoExtensions.has(extension)) {
    return "video";
  }
  if (normalizedMime.includes("pdf") || pdfExtensions.has(extension)) {
    return "pdf";
  }
  if (
    normalizedMime.includes("sheet") ||
    normalizedMime.includes("excel") ||
    normalizedMime.includes("spreadsheet") ||
    normalizedMime.includes("csv") ||
    spreadsheetExtensions.has(extension)
  ) {
    return "spreadsheet";
  }
  if (
    normalizedMime.includes("word") ||
    normalizedMime.includes("document") ||
    normalizedMime.includes("rtf") ||
    normalizedMime.startsWith("text/") ||
    documentExtensions.has(extension)
  ) {
    return "document";
  }
  if (
    normalizedMime.includes("presentation") ||
    normalizedMime.includes("powerpoint") ||
    presentationExtensions.has(extension)
  ) {
    return "presentation";
  }

  return "file";
}

export function isImageFile(mimeType?: string, fileName?: string) {
  return getMimeCategory(mimeType, fileName) === "image";
}

export function isVideoFile(mimeType?: string, fileName?: string) {
  return getMimeCategory(mimeType, fileName) === "video";
}

export function isPdfFile(mimeType?: string, fileName?: string) {
  return getMimeCategory(mimeType, fileName) === "pdf";
}

export function isOfficeFile(mimeType?: string, fileName?: string) {
  const category = getMimeCategory(mimeType, fileName);
  return category === "document" || category === "spreadsheet" || category === "presentation";
}
